/**
 * BriqueChart의 Sampling 기능 구현
 */

import BriqueChart from './BriqueChart.vue'
import { ToastProgrammatic as Toast } from 'buefy'

export default {
  /**
   * Data를 Sampling하여 리턴한다.
   * @param {Object} element Chart의 Element
   * @param {Integer} x0 Sampling할 범위의 x0값, 값이 없으면 초기데이터를 사용한다.
   * @param {Integer} y0 Sampling할 범위의 y0값, 값이 없으면 초기데이터를 사용한다.
   * @param {Integer} x1 Sampling할 범위의 x1값, 값이 없으면 초기데이터를 사용한다.
   * @param {Integer} y1 Sampling할 범위의 y1값, 값이 없으면 초기데이터를 사용한다.
   */
  getSamplingData (element, x0, y0, x1, y1) {
    x0 = x0 ?? element.layout?.xaxis?.range[0] ?? element.briqueChartData.xMinForPad
    x1 = x1 ?? element.layout?.xaxis?.range[element.layout.xaxis.range.length - 1] ?? element.briqueChartData.xMaxForPad

    y0 = y0 ?? element.layout?.yaxis?.range[0] ?? element.briqueChartData.yMinForPad
    y1 = y1 ?? element.layout?.yaxis?.range[element.layout.yaxis.range.length - 1] ?? element.briqueChartData.yMaxForPad

    let xRange = x1 - x0
    let yRange = y1 - y0

    let inner = element.querySelector(`.nsewdrag`) // 차트 안쪽 부분
    let width = inner?.getBBox().width || element.offsetWidth // 차트가 그려지기 전에는 inner가 없다.
    let height = inner?.getBBox().height || element.offsetHeight // 차트가 그려지기 전에는 inner가 없다.

    // original에서 x,y 제거
    let traces = []
    for (let trace of element.briqueChartData.tracesOriginalAfterXAxisDataSelection) {
      let { x, y, ...t } = trace
      traces.push(t)
    }

    for (let i = 0; i < element.briqueChartData.tracesOriginalAfterXAxisDataSelection.length; i++) {
      let oTrace = element.briqueChartData.tracesOriginalAfterXAxisDataSelection[i]
      let ox = oTrace.x
      let oy = oTrace.y
      let xLength = ox.length

      let markerSizePow = (oTrace.marker?.size || 6) ** 2 // Plotly의 default : 6
      let count = 0

      // let yLength = y.length
      let xOutput = []
      let yOutput = []
      let prevX = -9999
      let prevY = -9999
      for (let i = 0; i < xLength; i++) {
        if (ox[i] < x0 || ox[i] > x1) continue
        if (oy[i] < y0 || oy[i] > y1) continue

        count++
        let drawX = (ox[i] - x0) / xRange * width
        let drawY = (oy[i] - y0) / yRange * height
        if (((drawX - prevX) ** 2 + (drawY - prevY) ** 2) < markerSizePow) continue

        prevX = drawX
        prevY = drawY
        xOutput.push(ox[i])
        yOutput.push(oy[i])
      }

      traces[i].x = xOutput
      traces[i].y = yOutput

      console.log(`${oTrace.name} => 구간내 Original Count : ${count}, Sampling Count : ${xOutput.length}, rate : ${Math.round(xOutput.length / count * 100)}%`)
    }

    return { traces, x0, x1, y0, y1 }
  },
  /**
   * Data를 Sampling하여 차트에 적용한다.
   * @param {Object} element Chart의 Element
   * @param {Integer} x0 Sampling할 범위의 x0값, 값이 없으면 초기데이터를 사용한다.
   * @param {Integer} y0 Sampling할 범위의 y0값, 값이 없으면 초기데이터를 사용한다.
   * @param {Integer} x1 Sampling할 범위의 x1값, 값이 없으면 초기데이터를 사용한다.
   * @param {Integer} y1 Sampling할 범위의 y1값, 값이 없으면 초기데이터를 사용한다.
   * @param {Boolean} isShowLoadingBar 로딩바를 표시할지 여부
   */
  samplingData (element, x0, y0, x1, y1, isShowLoadingBar = true) {
    if (isShowLoadingBar) BriqueChart.G_LOADING_BAR.show()
    if (_.isString(element)) element = document.querySelector(`#${element}`)

    let promise = new Promise((resolve) => {
      setTimeout(async () => {
        let samplingDataBtn = BriqueChart.getModeBarButton(element, 'samplingData')

        if (samplingDataBtn) {
          BriqueChart.activeModeBarButton(element, samplingDataBtn)
        }

        let briqueChartData = element.briqueChartData
        briqueChartData.isSampling = true

        let samplingData = BriqueChart.getSamplingData(element, x0, y0, x1, y1)

        if (BriqueChart.isAfterNewPlot(element)) { // Chart를 그린적이 있다면
          element.layout.xaxis.range = [samplingData.x0, samplingData.x1]
          element.layout.yaxis.range = [samplingData.y0, samplingData.y1]

          BriqueChart.react(element, samplingData.traces, element.layout, element._context, undefined, isShowLoadingBar)
        } else {
          // BriqueChart.setXMinMax(element, samplingData.traces)
          // BriqueChart.setYMinMax(element, samplingData.traces)
        }

        resolve(samplingData.traces)

        if (isShowLoadingBar) BriqueChart.G_LOADING_BAR.hide()
      }, 50)
    })

    promise.then(() => {
      Toast.open({
        message: element.parentElement.__vue__.$t('fdc.CHART_COMPONENT.MESSAGE.INFORM_SHOW_SAMPLING'),
        type: 'is-success',
        queue: false // queue: false => toast가 2개 이상일 때 같은 영역에 덮어쓰지 않고, 최신것이 위에 표시되게 하는 방식
      })
    })

    return promise
  },

  /**
   * Data를 Sampling하여 차트에 적용한다.(500ms동안 1번만 호출)
   * @param {Object} element Chart의 Element
   * @param {Integer} x0 Sampling할 범위의 x0값, 값이 없으면 초기데이터를 사용한다.
   * @param {Integer} y0 Sampling할 범위의 y0값, 값이 없으면 초기데이터를 사용한다.
   * @param {Integer} x1 Sampling할 범위의 x1값, 값이 없으면 초기데이터를 사용한다.
   * @param {Integer} y1 Sampling할 범위의 y1값, 값이 없으면 초기데이터를 사용한다.
   * @param {Boolean} isShowLoadingBar 로딩바를 표시할지 여부
   */
  samplingDataDebounce: _.debounce(function (element, x0, y0, x1, y1, isShowLoadingBar = true) {
    if (_.isString(element)) element = document.querySelector(`#${element}`)

    BriqueChart.samplingData(...arguments).then(() => {
      // BriqueChart.restyleModeBarButtons(element)
    })
  }, 500),

  /**
   * 데이터 샘플링을 해제하고 차트에 적용한다.
   * @param {Object} element Chart Element
   * @param {Boolean} isShowLoadingBar 로딩바를 표시할지 여부
   */
  resetSamplingData (element, isShowLoadingBar = true) {
    if (_.isString(element)) element = document.querySelector(`#${element}`)

    element.briqueChartData.isSampling = false
    if (isShowLoadingBar) BriqueChart.G_LOADING_BAR.show()

    setTimeout(() => {
      let traces = _.merge([], element.data, element.briqueChartData.tracesOriginalAfterXAxisDataSelection)
      BriqueChart.setXMinMax(element, traces)
      BriqueChart.setYMinMax(element, traces)
      BriqueChart.react(element, traces, element.layout, element._context, undefined, isShowLoadingBar).then(() => {
        // BriqueChart.restyleModeBarButtons(element)
        Toast.open({
          message: element.parentElement.__vue__.$t('fdc.CHART_COMPONENT.MESSAGE.INFORM_SHOW_ALL'),
          type: 'is-success',
          queue: false // queue: false => toast가 2개 이상일 때 같은 영역에 덮어쓰지 않고, 최신것이 위에 표시되게 하는 방식
        })
      })

      if (isShowLoadingBar) BriqueChart.G_LOADING_BAR.hide()
    }, 50)
  }
}
